import React, { Component } from 'react'
import { Alert, Button, Card, Checkbox, Empty, Modal, Popover, Radio, Select, Space, Spin, Steps, Table, Tag, Tooltip, Typography, message } from 'antd'
import {
    CheckCircleOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReadOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    UserAddOutlined,
} from '@ant-design/icons'
import {
    deleteVibeOwnerPagePermission,
    getUserDeparts,
    getUserListForDropbox,
    getVibeOwnerAccessContext,
    getVibeOwnerAccessProjects,
    getVibeOwnerAccessStatus,
    requestVibeOwnerPageAccess,
    replaceVibeOwnerPagePermission,
} from '../../request'
import { buildDepartmentOptions, buildUserOptions } from './vibeOwnerAccessOptions'
import {
    buildApplicationEntryUrl,
    buildOwnerDirectEntryUrl,
    buildSafePagePreviewUrl,
    resolveSafeReturnPage,
} from './vibeOwnerAccessNavigation'
import {
    getDeniedReasonAction,
    getDeniedReasonContent,
    shouldPromptRouteResync,
} from './vibeOwnerAccessReason'
import {
    buildAccessRequestText,
    buildCurrentPageFromParams,
    buildPageAccessList,
    getCurrentPageHint,
    getOwnerLandingCopy,
    getPageDisplay,
    matchRouteByPage,
    resolveCurrentPage,
    summarizeGrantedPages,
} from './vibeOwnerAccessStatus'
import './index.less'

const { Title, Text, Paragraph } = Typography

const formatTime = value => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    const pad = number => String(number).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default class VibeOwnerAccess extends Component {
    state = {
        loading: true, error: '', projects: [], projectId: undefined, context: null, statusContext: null,
        selectedRouteIds: [], accessMode: undefined, subjectId: undefined,
        includeChildren: true, users: [], departments: [], saving: false,
        optionLoading: false,
        drawerOpen: false, drawerMode: 'create', editingPermission: null, deletingKey: null,
        accessRequestSubmitting: false, accessRequested: false,
        currentPage: null, focusedRouteId: null,
    }

    componentDidMount() {
        if (this.props.embedded && this.props.projectId) {
            this.requestedProjectId = String(this.props.projectId)
            this.setState({ projectId: this.props.projectId }, () => this.loadContext(this.props.projectId))
            return
        }
        const params = new URLSearchParams(this.props.location?.search || '')
        this.requestedProjectId = params.get('projectId')
        this.returnPage = params.get('returnPage') || ''
        this.currentPage = this.buildCurrentPage(params)
        this.setState({ currentPage: this.currentPage })
        this.deniedReason = params.get('reason') || ''
        this.statusView = params.get('view') === 'status'
            || this.props.location?.pathname === '/portal/vibe-access/denied'
        if (this.statusView) {
            this.loadStatusContext()
            return
        }
        this.loadProjects()
    }

    componentDidUpdate(prevProps) {
        if (this.props.embedded
            && String(prevProps.projectId || '') !== String(this.props.projectId || '')
            && this.props.projectId) {
            this.requestedProjectId = String(this.props.projectId)
            this.setState({ projectId: this.props.projectId }, () => this.loadContext(this.props.projectId))
        }
    }

    loadStatusContext = async () => {
        if (!/^\d+$/.test(this.requestedProjectId || '')) {
            this.setState({ loading: false, error: '缺少应用信息。请从被拦截的页面进入，或打开应用权限设置页。' })
            return
        }
        this.setState({ loading: true, error: '' })
        try {
            const statusContext = await getVibeOwnerAccessStatus({
                projectId: this.requestedProjectId,
                returnPage: this.returnPage,
            })
            this.setState({ loading: false, statusContext })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用状态加载失败，请稍后重试' })
        }
    }

    copyAccessRequest = async () => {
        try {
            const currentPage = resolveCurrentPage(this.state.currentPage, this.state.statusContext)
            await navigator.clipboard.writeText(buildAccessRequestText({
                projectName: this.state.statusContext?.projectName,
                ownerUserNick: this.state.statusContext?.ownerUserNick,
                pageName: currentPage?.name,
                pagePath: currentPage?.path,
            }))
            message.success('申请信息已复制，可以直接发给应用负责人')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    buildCurrentPage = params => buildCurrentPageFromParams(params)

    getEntryUrl = () => this.state.context?.entryUrl || this.state.statusContext?.entryUrl || ''

    openPagePreview = page => {
        const display = getPageDisplay(page || {})
        const entryUrl = this.getEntryUrl()
        const safePreviewUrl = (page?.previewUrl ? resolveSafeReturnPage(page.previewUrl, entryUrl) : '')
            || (page?.url ? resolveSafeReturnPage(page.url, entryUrl) : '')
            || buildSafePagePreviewUrl(entryUrl, page?.pathPattern || page?.path || page?.url || display.path)
        const previewUrl = this.statusView ? safePreviewUrl : buildOwnerDirectEntryUrl(safePreviewUrl)
        if (!previewUrl) {
            message.warning('暂时无法预览这个页面')
            return
        }
        window.open(previewUrl, '_blank', 'noopener,noreferrer')
    }

    previewOwnerPage = (routes = []) => {
        const focused = routes.find(route => String(route.id) === String(this.state.focusedRouteId))
            || matchRouteByPage(routes, this.state.currentPage)
            || routes[0]
            || this.state.currentPage
        this.openPagePreview(focused)
    }

    renderPreviewButton = (page, size = 'small', enabled = true) => (
        <Button
            type="link"
            size={size}
            icon={<EyeOutlined />}
            disabled={!enabled}
            title={enabled ? '打开这个页面' : '开通后就可以打开'}
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                if (!enabled) return
                this.openPagePreview(page)
            }}
        >
            预览
        </Button>
    )

    renderCurrentPage = (currentPage, statusContext, role = 'apply') => {
        const page = resolveCurrentPage(currentPage, statusContext)
        if (!page) return null
        const currentRow = buildPageAccessList(currentPage, statusContext).find(row => row.current)
        const granted = role === 'grant' || currentRow?.status === 'granted'
        const hint = role === 'apply' ? getCurrentPageHint(this.deniedReason, page) : ''
        return <div className={`vibe-owner-access-current-page${role === 'grant' ? ' is-grant' : granted ? ' is-opened' : ' is-blocked'}`}>
            <div className="vibe-owner-access-current-page-icon">
                {granted && role !== 'grant' ? <CheckCircleOutlined /> : <ReadOutlined />}
            </div>
            <div className="vibe-owner-access-current-page-copy">
                <div className="vibe-owner-access-current-page-title">
                    <Text strong>{role === 'grant' ? '当前页面' : '刚才打开的页面'}</Text>
                    {role !== 'grant' && <Tag color={granted ? 'green' : 'orange'}>
                        {granted ? '已开通' : '没有权限'}
                    </Tag>}
                </div>
                <Text className="vibe-owner-access-current-page-name">{page.name}</Text>
                {page.description
                    ? <Text type="secondary">{page.description}</Text>
                    : role !== 'grant' ? <Text type="secondary">{granted ? '这个页面已经可以打开。' : '这个页面还没有对你开放。'}</Text> : null}
                {hint && !granted && <Text className="vibe-owner-access-current-page-hint">{hint}</Text>}
            </div>
            {role !== 'grant' && this.renderPreviewButton({ ...page, url: page.url || currentPage?.url }, 'middle', granted)}
        </div>
    }

    renderPageAccessList = (currentPage, statusContext) => {
        const rows = buildPageAccessList(currentPage, statusContext).filter(row => !row.current && row.status === 'granted')
        return <div className="vibe-owner-access-page-list">
            <div className="vibe-owner-access-section-heading">
                <div>
                    <Title level={5}>你已经能打开的页面</Title>
                    <Text type="secondary">这些页面不用再申请，可以直接打开。</Text>
                </div>
                <Tag color={rows.length ? 'green' : 'default'}>{rows.length ? `已开通 ${rows.length} 个` : '暂无其他页面'}</Tag>
            </div>
            {rows.length ? <div className="vibe-owner-access-page-list-body">{rows.map(row => (
                <div className="vibe-owner-access-page-row" key={row.key}>
                    <div className="vibe-owner-access-page-status">
                        <CheckCircleOutlined />
                    </div>
                    <div className="vibe-owner-access-page-copy">
                        <div className="vibe-owner-access-page-title">
                            <Text strong>{row.name}</Text>
                            <Tag color="green">已开通</Tag>
                        </div>
                        {row.description
                            ? <Text type="secondary">{row.description}</Text>
                            : <Text type="secondary">你已经可以打开这个页面。</Text>}
                    </div>
                    {this.renderPreviewButton(row, 'small', true)}
                </div>
            ))}</div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="除了刚才这个页面，你还没有其他已开通的页面。" />}
        </div>
    }

    requestAccess = async () => {
        if (!this.requestedProjectId || this.state.accessRequestSubmitting || this.state.accessRequested) return
        this.setState({ accessRequestSubmitting: true })
        try {
            const currentPage = resolveCurrentPage(this.state.currentPage, this.state.statusContext)
            await requestVibeOwnerPageAccess({
                projectId: this.requestedProjectId,
                returnPage: this.returnPage,
                pageName: currentPage?.name,
                pagePath: currentPage?.path,
            })
            this.setState({ accessRequestSubmitting: false, accessRequested: true })
            message.success('申请已发送，已通过钉钉提醒应用负责人')
        } catch (error) {
            this.setState({ accessRequestSubmitting: false })
            message.error(error?.message || '申请发送失败，请稍后重试')
        }
    }

    loadProjects = async () => {
        this.setState({ loading: true, error: '' })
        try {
            const projects = await getVibeOwnerAccessProjects() || []
            const requested = projects.find(item => String(item.id) === String(this.requestedProjectId))
            const project = requested || projects[0]
            this.setState({ projects, projectId: project?.id, loading: false }, () => {
                if (project) this.loadContext(project.id)
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用列表加载失败，请稍后重试' })
        }
    }

    loadContext = async (projectId = this.state.projectId) => {
        if (!projectId) return
        this.setState({ loading: true, error: '', projectId })
        try {
            const context = await getVibeOwnerAccessContext({ projectId })
            const pageRoutes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
            const focusedRoute = matchRouteByPage(pageRoutes, this.currentPage)
            this.setState({
                context,
                selectedRouteIds: pageRoutes.map(route => route.id),
                accessMode: undefined,
                subjectId: undefined,
                drawerOpen: false,
                editingPermission: null,
                focusedRouteId: focusedRoute?.id || null,
                loading: false,
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '权限设置加载失败，请稍后重试' })
        }
    }

    loadOptions = async mode => {
        this.setState({ accessMode: mode, subjectId: undefined })
        if (mode === 'ALL' || (this.state.users.length && this.state.departments.length)) return
        this.setState({ optionLoading: true })
        try {
            const [users, departments] = await Promise.all([
                getUserListForDropbox({ type: 0 }), getUserDeparts({}),
            ])
            this.setState({ users: users || [], departments: departments || [] })
        } catch (error) {
            message.error(error?.message || '授权对象加载失败')
        } finally {
            this.setState({ optionLoading: false })
        }
    }

    copyPrompt = async () => {
        const prompt = this.state.context?.routeSyncPrompt || ''
        if (!prompt) {
            message.warning('暂时没有可复制的提示词')
            return
        }
        try {
            await navigator.clipboard.writeText(prompt)
            message.success('提示词已复制，到项目 Codex 里执行')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    renderRouteSyncFooter = () => {
        const prompt = this.state.context?.routeSyncPrompt || ''
        return <div className="vibe-owner-access-sync-footer">
            <div className="vibe-owner-access-sync-heading">
                <div>
                    <Title level={5}>同步页面</Title>
                    <Text type="secondary">有新页面时，复制提示词到项目 Codex 执行。</Text>
                </div>
                <Button disabled={!prompt} onClick={this.copyPrompt}>复制提示词</Button>
            </div>
            <div className="vibe-owner-access-sync-prompt-wrap">
                <pre className="vibe-owner-access-sync-prompt">{prompt || '暂时没有可复制的提示词'}</pre>
                <Button
                    type="text"
                    size="small"
                    className="vibe-owner-access-sync-copy"
                    icon={<CopyOutlined />}
                    disabled={!prompt}
                    onClick={this.copyPrompt}
                />
            </div>
        </div>
    }

    openCreateDrawer = () => {
        const routes = (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE')
        const focusedRoute = matchRouteByPage(routes, this.currentPage)
        const useFocusedOnly = this.deniedReason === 'PAGE_PERMISSION_NOT_GRANTED' && focusedRoute
        this.setState({
            drawerOpen: true, drawerMode: 'create', editingPermission: null,
            selectedRouteIds: useFocusedOnly ? [focusedRoute.id] : routes.map(route => route.id),
            accessMode: undefined, subjectId: undefined, includeChildren: true,
            focusedRouteId: focusedRoute?.id || this.state.focusedRouteId,
        })
    }

    openEditDrawer = row => {
        this.setState({
            drawerOpen: true, drawerMode: 'edit', editingPermission: row,
            selectedRouteIds: row.routes.map(route => route.id), accessMode: row.subjectType,
            subjectId: row.subjectId, includeChildren: row.includeChildren !== false,
        })
    }

    closeDrawer = () => {
        if (!this.state.saving) this.setState({ drawerOpen: false, editingPermission: null })
    }

    savePermissionGroup = async () => {
        const { projectId, selectedRouteIds, accessMode, subjectId, includeChildren } = this.state
        if (!accessMode) {
            message.warning('请先选择授权对象')
            return
        }
        if (!subjectId && accessMode !== 'ALL') {
            message.warning('请选择同事或部门')
            return
        }
        this.setState({ saving: true })
        try {
            await replaceVibeOwnerPagePermission({
                projectId, routeIds: selectedRouteIds, subjectType: accessMode,
                subjectId: accessMode === 'ALL' ? '' : String(subjectId), includeChildren,
            })
            message.success(this.state.drawerMode === 'edit' ? '授权已更新' : '授权已添加')
            this.setState({ saving: false, drawerOpen: false, editingPermission: null }, () => this.loadContext())
        } catch (error) {
            this.setState({ saving: false })
            message.error(error?.message || '保存失败，请稍后重试')
        }
    }

    deletePermissionGroup = row => {
        const explicitCount = this.getPermissionRows(this.state.context,
            (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE'))
            .filter(item => item.subjectType !== 'OWNER').length
        Modal.confirm({
            title: `取消“${row.subjectName}”的授权？`,
            content: <div><Paragraph>取消后，对方将不能再访问当前应用的 {row.routes.length} 个页面。</Paragraph>
                {explicitCount === 1 && <Alert type="warning" showIcon message="这是最后一条授权，取消后其他人将暂时无法访问这个应用。" />}</div>,
            okText: '确认取消授权', okButtonProps: { danger: true }, cancelText: '再想想',
            onOk: async () => {
                this.setState({ deletingKey: row.key })
                try {
                    await deleteVibeOwnerPagePermission({
                        projectId: this.state.projectId, subjectType: row.subjectType,
                        subjectId: row.subjectId || '', includeChildren: row.includeChildren === true,
                    })
                    message.success('授权已取消')
                    this.setState({ deletingKey: null }, () => this.loadContext())
                } catch (error) {
                    this.setState({ deletingKey: null })
                    message.error(error?.message || '删除失败，请稍后重试')
                    throw error
                }
            },
        })
    }

    getPermissionRows = (context, routes) => {
        if (!context) return []
        const grouped = new Map()
        routes.forEach(route => {
            ;(route.permissions || []).forEach(permission => {
                if (permission.subjectType === 'USER'
                    && String(permission.subjectId) === String(context.ownerUserId)) return
                const key = `${permission.subjectType}:${permission.subjectId || ''}`
                const existing = grouped.get(key) || {
                    key,
                    subjectType: permission.subjectType,
                    subjectId: permission.subjectId || '',
                    subjectName: permission.subjectName || permission.subjectId,
                    includeChildren: permission.includeChildren,
                    routes: [],
                    createdAt: permission.createdAt,
                }
                existing.routes.push(route)
                if (permission.createdAt && (!existing.createdAt || String(permission.createdAt) < String(existing.createdAt))) {
                    existing.createdAt = permission.createdAt
                }
                grouped.set(key, existing)
            })
        })
        return [{
            key: 'OWNER', subjectType: 'OWNER',
            subjectName: context.ownerUserNick || '项目创建人',
            routes, createdAt: context.createTime,
        }, ...Array.from(grouped.values())]
    }

    getApplicationEntryUrl = context => buildApplicationEntryUrl(context?.entryUrl)

    renderPermissionDrawer = routes => {
        const editing = this.state.drawerMode === 'edit'
        const typeName = { ALL: '全部员工', USER: '指定员工', DEPARTMENT: '指定部门' }
        const ownerUserId = String(this.state.context?.ownerUserId || '')
        const selectedIds = this.state.selectedRouteIds
        return <Modal
            title={editing ? '修改授权' : '新增授权'}
            width={760}
            visible={this.state.drawerOpen}
            onCancel={this.closeDrawer}
            destroyOnClose
            maskClosable={!this.state.saving}
            bodyStyle={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}
            confirmLoading={this.state.saving}
            okText={editing ? '保存修改' : '确认授权'}
            cancelText="取消"
            okButtonProps={{
                disabled: !selectedIds.length
                    || !this.state.accessMode
                    || (!this.state.subjectId && this.state.accessMode !== 'ALL'),
            }}
            onOk={this.savePermissionGroup}
        >
            <div className="vibe-owner-access-drawer-intro">
                <Text>先选择谁可以访问，再勾选对方能打开的页面。</Text>
                <Text type="secondary">不确定页面是什么，可以先预览再保存。</Text>
            </div>
            <div className="vibe-owner-access-grant">
                <Title level={5}>1. 选择授权对象</Title>
                {editing ? <div className="vibe-owner-access-locked-subject"><Text strong>{this.state.editingPermission?.subjectName}</Text>
                    <Tag>{typeName[this.state.accessMode]}</Tag><Text type="secondary">如需更换对象，请取消后重新授权。</Text></div>
                    : <Radio.Group value={this.state.accessMode} onChange={event => this.loadOptions(event.target.value)}>
                        <Radio value="USER">指定员工</Radio><Radio value="DEPARTMENT">指定部门</Radio><Radio value="ALL">全部员工</Radio>
                    </Radio.Group>}
                {!editing && this.state.accessMode === 'USER' && <Select showSearch placeholder="选择同事" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                    loading={this.state.optionLoading}
                    notFoundContent={this.state.optionLoading ? '正在加载员工…' : '没有可选择的员工'}
                    optionFilterProp="label"
                    options={buildUserOptions(this.state.users)
                        .filter(option => String(option.value) !== ownerUserId)} />}
                {this.state.accessMode === 'DEPARTMENT' && <Space>{!editing && <Select showSearch placeholder="选择部门" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                    loading={this.state.optionLoading}
                    notFoundContent={this.state.optionLoading ? '正在加载部门…' : '没有可选择的部门'}
                    optionFilterProp="label" options={buildDepartmentOptions(this.state.departments)} />}
                    <Checkbox checked={this.state.includeChildren} onChange={event => this.setState({ includeChildren: event.target.checked })}>包含下级部门</Checkbox></Space>}
                <Paragraph type="secondary">项目创建人始终可以打开全部页面，不用再给自己授权。</Paragraph>
            </div>
            <div className="vibe-owner-access-routes">
                <div className="vibe-owner-access-section-heading">
                    <div>
                        <Title level={5}>2. 选择可访问页面</Title>
                        <Text type="secondary">已选 {selectedIds.length} / {routes.length} 个页面</Text>
                    </div>
                </div>
                <div className="vibe-owner-access-route-list">
                    {routes.map(route => {
                        const checked = selectedIds.includes(route.id)
                        const focused = String(route.id) === String(this.state.focusedRouteId)
                        return <div
                            key={route.id}
                            className={`vibe-owner-access-route-item${checked ? ' is-checked' : ''}${focused ? ' is-focus' : ''}`}
                        >
                            <Checkbox
                                checked={checked}
                                onChange={event => this.setState({
                                    selectedRouteIds: event.target.checked
                                        ? [...selectedIds, route.id]
                                        : selectedIds.filter(id => id !== route.id),
                                })}
                            >
                                <div className="vibe-owner-access-route-copy">
                                    <Text strong>{route.routeName}</Text>
                                    <Text type="secondary" code>{route.pathPattern}</Text>
                                    {focused && <Tag color="gold">这次要处理</Tag>}
                                </div>
                            </Checkbox>
                            {this.renderPreviewButton(route)}
                        </div>
                    })}
                </div>
            </div>
        </Modal>
    }

    renderGrantedPagesPopover = routeList => (
        <div className="vibe-owner-access-page-popover">
            {(routeList || []).map(route => (
                <div className="vibe-owner-access-page-popover-item" key={route.id || route.pathPattern}>
                    <div>
                        <Text strong>{route.routeName || route.name}</Text>
                        {route.description ? <Text type="secondary">{route.description}</Text> : null}
                    </div>
                    {this.renderPreviewButton(route)}
                </div>
            ))}
        </div>
    )

    renderGrantedPagesSummary = (routeList, allRoutes) => {
        const summary = summarizeGrantedPages(routeList, allRoutes)
        if (!summary.count) return <Text type="secondary">{summary.label}</Text>
        return <Popover
            trigger={['hover', 'click']}
            placement="bottomLeft"
            overlayClassName="vibe-owner-access-page-popover-overlay"
            content={this.renderGrantedPagesPopover(routeList)}
        >
            <button type="button" className={`vibe-owner-access-page-summary${summary.all ? ' is-all' : ''}`}>
                {summary.label}
            </button>
        </Popover>
    }

    renderPermissionList = (context, routes) => {
        const rows = this.getPermissionRows(context, routes)
        const applicationEntryUrl = this.getApplicationEntryUrl(context)
        const landingCopy = getOwnerLandingCopy(this.deniedReason, { accessConfigured: context?.accessConfigured })
        const typeMeta = {
            OWNER: { color: 'gold', text: '项目创建人' },
            USER: { color: 'blue', text: '员工' },
            DEPARTMENT: { color: 'purple', text: '部门' },
            ALL: { color: 'green', text: '全部员工' },
        }
        const columns = [{
            title: '授权对象', dataIndex: 'subjectName', width: 220,
            render: (value, row) => <div className="vibe-owner-access-subject"><Text strong>{value}</Text>
                <Tag color={typeMeta[row.subjectType]?.color}>{typeMeta[row.subjectType]?.text || row.subjectType}</Tag>
                {row.subjectType === 'DEPARTMENT' && row.includeChildren ? <Text type="secondary">包含下级部门</Text> : null}</div>,
        }, {
            title: '可访问页面', dataIndex: 'routes',
            render: routeList => this.renderGrantedPagesSummary(routeList, routes),
        }, {
            title: '授权时间', dataIndex: 'createdAt', width: 170,
            render: value => <Text type="secondary">{formatTime(value)}</Text>,
        }, {
            title: '操作', key: 'action', width: 150, fixed: 'right',
            render: (_, row) => row.subjectType === 'OWNER' ? null : <Space>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => this.openEditDrawer(row)}>修改</Button>
                <Button type="link" size="small" danger icon={<DeleteOutlined />} loading={this.state.deletingKey === row.key}
                    onClick={() => this.deletePermissionGroup(row)}>取消授权</Button>
            </Space>,
        }]
        return <div className="vibe-owner-access-list">
            {landingCopy && <Alert className="vibe-owner-access-initial-alert" type="info" showIcon
                message={landingCopy.title}
                description={landingCopy.description}
                action={<Button type="primary" icon={<PlusOutlined />} onClick={this.openCreateDrawer}>新增授权</Button>} />}
            {this.renderCurrentPage(this.state.currentPage, { currentRoute: matchRouteByPage(routes, this.state.currentPage) }, 'grant')}
            {shouldPromptRouteResync(this.deniedReason) && <Alert className="vibe-owner-access-initial-alert" type="warning" showIcon
                message="当前访问的页面尚未上报"
                description="请重新执行页面同步，让新页面进入访问清单；已有页面权限不会受影响。"
                action={<Space direction="vertical"><Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制页面同步提示词</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => this.loadContext()}>我已同步，重新检测</Button></Space>} />}
            {!context?.accessConfigured && !landingCopy && <div className="vibe-owner-access-first-setup">
                <div className="vibe-owner-access-first-setup-icon"><UserAddOutlined /></div>
                <div className="vibe-owner-access-first-setup-copy">
                        <Text strong>先确认页面，再添加访问权限</Text>
                        <Text type="secondary">确认页面没问题后，再选择同事或部门，设置他们能访问的页面。</Text>
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreateDrawer}>新增授权</Button>
            </div>}
            <div className="vibe-owner-access-list-panel">
                <div className="vibe-owner-access-list-heading">
                    <div>
                        <Title level={5}>授权记录</Title>
                    </div>
                    <Space>
                        <Button icon={<EyeOutlined />} onClick={() => this.previewOwnerPage(routes)}>预览</Button>
                        <Button disabled={!applicationEntryUrl} onClick={() => window.location.assign(applicationEntryUrl)}>
                            打开应用
                        </Button>
                        {context?.accessConfigured && <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreateDrawer}>新增授权</Button>}
                    </Space>
                </div>
                <Table rowKey="key" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 760 }} />
            </div>
            {this.renderRouteSyncFooter()}
        </div>
    }

    renderStatus = () => {
        const { loading, error, statusContext, accessRequestSubmitting, accessRequested, currentPage } = this.state
        const reasonContent = getDeniedReasonContent(this.deniedReason)
        const actionContent = getDeniedReasonAction(this.deniedReason)
        const focusedPage = resolveCurrentPage(currentPage, statusContext)
        const statusDescription = focusedPage?.name
            ? `刚才打开的是「${focusedPage.name}」。${reasonContent.description}`
            : reasonContent.description
        const retryUrl = resolveSafeReturnPage(this.returnPage, statusContext?.entryUrl)
            || statusContext?.entryUrl
        return (
        <div className="vibe-owner-access-page"><Card className="vibe-owner-access-status">
            <Spin spinning={loading}>
                <div className="vibe-owner-access-status-heading">
                    <div className="vibe-owner-access-status-icon"><SafetyCertificateOutlined /></div>
                    <div className="vibe-owner-access-status-title">
                        <Text className="vibe-owner-access-status-kicker">申请权限</Text>
                        <Title level={3}>{reasonContent.title}</Title>
                    </div>
                    <Tag color="orange">申请</Tag>
                </div>
                {error ? <Alert type="error" showIcon message={error} /> : <>
                    <Paragraph className="vibe-owner-access-status-description">{statusDescription}</Paragraph>
                    <div className="vibe-owner-access-status-summary">
                        <div><Text type="secondary">要申请的应用</Text><Text strong>{statusContext?.projectName || '当前应用'}</Text></div>
                        <div><Text type="secondary">应用负责人</Text><Text strong>{statusContext?.ownerUserNick || '请联系应用负责人'}</Text></div>
                    </div>
                    {this.renderCurrentPage(currentPage, statusContext, 'apply')}
                    {this.renderPageAccessList(currentPage, statusContext)}
                    <Paragraph className="vibe-owner-access-status-next">下一步：点下方“{actionContent.requestLabel}”，给负责人发钉钉开通权限，开通后再点“重新检查权限”。</Paragraph>
                    <div className="vibe-owner-access-status-guide">
                        <div className="vibe-owner-access-status-guide-title">
                            <Text strong>{actionContent.guideTitle}</Text>
                            <Button type="link" size="small" icon={<CopyOutlined />} onClick={this.copyAccessRequest}>复制申请信息</Button>
                        </div>
                        <div className="vibe-owner-access-status-steps">
                            {actionContent.steps.map((step, index) => <div key={step}>
                                <span className="vibe-owner-access-status-step-number">{index + 1}</span>
                                <Text className="vibe-owner-access-status-step-text">{step}</Text>
                            </div>)}
                        </div>
                    </div>
                </>}
                {accessRequested && <Alert className="vibe-owner-access-request-success" type="success" showIcon
                    icon={<CheckCircleOutlined />} message="申请已发出"
                    description="应用负责人已收到钉钉开通权限消息。开通完成后，请点击“重新检查权限”。" />}
                <Space className="vibe-owner-access-status-actions" wrap>
                    {!error && <Tooltip title={accessRequested ? '开通权限消息已发送' : actionContent.requestHint}>
                        <span>
                            <Button type="primary" loading={accessRequestSubmitting} disabled={accessRequested}
                                onClick={this.requestAccess}>{accessRequested ? actionContent.submittedLabel : actionContent.requestLabel}</Button>
                        </span>
                    </Tooltip>}
                    <Button disabled={!retryUrl} onClick={() => window.location.assign(retryUrl)}>重新检查权限</Button>
                    <Button href="/portal/dashboard">返回工作台</Button>
                    {error && <Button onClick={this.loadStatusContext}>重试加载</Button>}
                </Space>
            </Spin>
        </Card></div>
        )
    }

    render() {
        const { loading, error, projects, projectId, context } = this.state
        const routes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
        if (this.statusView) return this.renderStatus()
        if (this.props.embedded) {
            return <div className="vibe-owner-access-embedded">
                <Spin spinning={loading}>
                    <div className="vibe-owner-access-embedded-intro">
                        <div>
                            <Text type="secondary">页面授权</Text>
                        </div>
                        <Tag color={context?.accessConfigured ? 'green' : 'gold'}>
                            {context?.accessConfigured ? '已设置' : '未设置'}
                        </Tag>
                    </div>
                    {error && <Alert type="error" showIcon message={error}
                        action={<Button onClick={() => this.loadContext(projectId)}>重试</Button>} />}
                    {!error && !loading && !context && <Empty description="暂时无法获取应用权限信息" />}
                    {!error && context && !routes.length && <>
                        <Steps current={0} items={[{ title: '同步页面' }, { title: '配置授权' }, { title: '完成设置' }]} />
                        <Alert className="vibe-owner-access-alert" type="warning" showIcon
                            message="还差一步即可完成设置"
                            description="尚未检测到应用页面。复制提示词到项目 Codex 执行，完成后重新检测。"
                            action={<Space direction="vertical"><Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制页面同步提示词</Button>
                                <Button icon={<ReloadOutlined />} onClick={() => this.loadContext(projectId)}>我已同步，重新检测</Button></Space>} />
                        {this.renderRouteSyncFooter()}
                    </>}
                    {!!routes.length && this.renderPermissionList(context, routes)}
                    {!!routes.length && this.renderPermissionDrawer(routes)}
                </Spin>
            </div>
        }
        return <div className="vibe-owner-access-page">
            <div className="vibe-owner-access-heading">
                <div className="vibe-owner-access-heading-icon"><SafetyCertificateOutlined /></div>
                <div>
                    <div className="vibe-owner-access-heading-title">
                        <Title level={3}>页面授权</Title>
                    </div>
                </div>
            </div>
            <Card bordered={false} className="vibe-owner-access-card">
                <Spin spinning={loading}>
                    {error && <Alert type="error" showIcon message={error} action={<Button onClick={this.loadProjects}>重试</Button>} />}
                    {!error && !loading && !projects.length && <Empty description="你还没有可配置的内部应用" />}
                    {!!projects.length && <>
                        <div className="vibe-owner-access-project">
                            <div className="vibe-owner-access-project-label">
                                <Text type="secondary">当前应用</Text>
                                <Text strong>我创建的应用</Text>
                            </div>
                            <div className="vibe-owner-access-project-control">
                                {context && <Tag color={context.accessConfigured ? 'green' : 'gold'}>
                                    {context.accessConfigured ? '已设置' : '未设置'}
                                </Tag>}
                                <Select value={projectId} onChange={this.loadContext} style={{ minWidth: 260 }}
                                    options={projects.map(item => ({ value: item.id, label: item.projectName }))} />
                            </div>
                        </div>
                        {!context?.accessConfigured && <Steps className="vibe-owner-access-steps" current={routes.length ? 1 : 0} items={[{ title: '同步页面' }, { title: '配置授权' }, { title: '完成设置' }]} />}
                        {context && !routes.length && <>
                            <Alert className="vibe-owner-access-alert" type="warning" showIcon
                                message="还差一步即可完成设置"
                                description="尚未检测到应用页面。复制提示词到项目 Codex 执行，完成后重新检测。"
                                action={<Space direction="vertical"><Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制页面同步提示词</Button>
                                    <Button icon={<ReloadOutlined />} onClick={() => this.loadContext()}>我已同步，重新检测</Button></Space>} />
                            {this.renderRouteSyncFooter()}
                        </>}
                        {!!routes.length && this.renderPermissionList(context, routes)}
                        {!!routes.length && this.renderPermissionDrawer(routes)}
                    </>}
                </Spin>
            </Card>
        </div>
    }
}
